import { FC, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchPayslips } from '../../lib/payslips';
import { supabase } from '../../lib/supabase';
import type { Payslip } from '../../types/payslip';
import thirstyLogo from '../../assets/thirsty-logo.png'; // Make sure to add your logo

const formatPeriod = (period: string) => {
  // Assuming period comes as "January 16" or similar
  const [month, day] = period.split(' ');
  const year = new Date().getFullYear(); // Gets current year
  
  // If it's a start-of-month period (1-15)
  if (parseInt(day) <= 15) {
    return `${month} 1-15, ${year}`;
  }
  // If it's an end-of-month period (16-end)
  return `${month} 16-31, ${year}`;
};

const Payslips: FC = () => {
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    const loadPayslips = async () => {
      if (!user) return;
      try {
        setLoading(true);
        const data = await fetchPayslips(user.id);
        setPayslips(data || []);

        // Fetch user's display name from user_profiles
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('display_name')
          .eq('user_id', user.id)
          .single();

        if (profileError) {
          console.error('Error fetching user profile:', profileError);
        } else if (profileData) {
          setDisplayName(profileData.display_name);
        }
      } catch (error) {
        console.error('Error loading payslips:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPayslips();
  }, [user]);

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-8">
      {payslips.map((payslip) => (
        <div
          key={`${payslip.user_id}-${payslip.period}`}
          className="bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none"
        >
          {/* Header with Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <img 
                src={thirstyLogo} 
                alt="Thirsty Logo" 
                className="h-16 md:h-20"
              />
              <div className="text-right">
                <h2 className="text-2xl font-bold">Payslip</h2>
              </div>
            </div>
          </div>

          {/* Employee Info */}
          <div className="p-6 bg-gray-50">
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <p className="text-gray-600">Period</p>
                <p className="font-semibold">{formatPeriod(payslip.period)}</p>
              </div>
              <div>
                <p className="text-gray-600">Name</p>
                <p className="font-semibold">
                  {displayName || 'Employee'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Daily Rate</p>
                <p className="font-semibold">₱{payslip.daily_rate.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-gray-600">Total Hours</p>
                <p className="font-semibold">{payslip.total_hours} hrs</p>
              </div>
              <div>
                <p className="text-gray-600">Overtime Hours</p>
                <p className="font-semibold">{payslip.overtime_hours} hrs</p>
              </div>
            </div>
          </div>

          {/* Earnings Section */}
          <div className="p-6">
            <div className="space-y-4">
              {/* Earnings */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Earnings</h3>
                <div className="space-y-2">
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">Basic Pay</span>
                    <span className="font-medium">₱{payslip.basic_pay.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">Holiday Pay</span>
                    <span className="font-medium">₱{payslip.holiday_pay.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">Incentives</span>
                    <span className="font-medium">₱{payslip.incentives.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">Other Bonuses</span>
                    <span className="font-medium">₱{payslip.other_bonuses.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between pt-2 font-semibold">
                    <span>Total Gross Pay</span>
                    <span>₱{payslip.total_gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">Deductions</h3>
                <div className="space-y-2">
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">SSS</span>
                    <span className="font-medium">₱{payslip.sss.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">Pagibig</span>
                    <span className="font-medium">₱{payslip.pagibig.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">Philhealth</span>
                    <span className="font-medium">₱{payslip.philhealth.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">CA</span>
                    <span className="font-medium">₱{payslip.ca.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">Loan Deduction</span>
                    <span className="font-medium">₱{payslip.loan_deduction.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">Other Deductions</span>
                    <span className="font-medium">₱{payslip.other_deductions.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between pt-2 font-semibold">
                    <span>Total Deductions</span>
                    <span>₱{payslip.total_deductions.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">NET PAY</span>
                  <span className="text-xl font-bold text-green-600">
                    ₱{payslip.net_pay.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Payslips; 